/**
 * Attachment indexing (processing) status — one source of truth.
 *
 * The AI-API marks an uploaded attachment ready when its `processing_status`
 * enum reaches `READY`; pre-migration rows have a NULL status and are treated
 * as ready (mirrors `AttachmentRepositoryPort.all_ready`). Endpoints return the
 * raw row, so the field can arrive snake_case or camelCase — the readers below
 * tolerate both. Keep every readiness check here so the poll loops that consume
 * different endpoints (scope files, project files, readiness gate) agree.
 *
 * @module @ideoz/core/utils/attachment-readiness
 */

import { fixFilenameMojibake } from './filename';

export const ATTACHMENT_READY_STATUS = 'READY';

/** Per-file display state derived from the backend processing status. */
export type FileIndexState = 'indexed' | 'analyzing' | 'pending';

/** Read the processing status off a raw attachment row (snake or camel). */
export const readAttachmentStatus = (row: any): string | null | undefined =>
  row?.processing_status ?? row?.processingStatus ?? row?.status;

/** Read a human file name off a raw attachment row, tolerant of key casing. */
export const readAttachmentName = (row: any): string =>
  fixFilenameMojibake(
    row?.file_name ?? row?.fileName ?? row?.name ?? row?.original_name ?? row?.originalName ?? 'File'
  );

/** A missing status is a pre-migration row → treated as ready. */
export const isAttachmentReady = (status: string | null | undefined): boolean =>
  !status || String(status).toUpperCase() === ATTACHMENT_READY_STATUS;

/** Map a processing status to the three-state UI label. */
export const toFileIndexState = (status: string | null | undefined): FileIndexState => {
  const s = String(status ?? '').toUpperCase();
  if (!status || s === ATTACHMENT_READY_STATUS) return 'indexed';
  if (s === 'PROCESSING' || s === 'ANALYZING' || s === 'RUNNING' || s === 'IN_PROGRESS')
    return 'analyzing';
  return 'pending'; // PENDING / QUEUED / unknown
};
